import { z } from 'zod';
import { departmentByCode, departmentOfMunicipality, formatNit, municipalityByCode, nitCheckDigit, nitDigits } from '@xiltrion/colombia';
/**
 * Ficha de la entidad (GUIA-AGENTES.md §3.11): los datos de la organización que todas las apps muestran o imprimen.
 * El nombre, el NIT y el perfil (empresa, entidad pública, ONG) siguen en la entidad; aquí va el resto.
 */
const optionalText = (max) => z.string().trim().max(max).optional().nullable().transform((v) => v || null);
/** Zona horaria IANA que el motor conoce (`America/Bogota`). */
export function isTimeZone(tz) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
export const DEFAULT_TIMEZONE = 'America/Bogota';
export const DEFAULT_CURRENCY = 'COP';
export const DEFAULT_LOCALE = 'es-CO';
/** Zonas que se ofrecen primero (cualquier otra IANA también vale). */
export const COMMON_TIMEZONES = [
    'America/Bogota', 'America/Mexico_City', 'America/Lima', 'America/Guayaquil', 'America/Panama', 'America/Caracas',
    'America/Santiago', 'America/Argentina/Buenos_Aires', 'America/Sao_Paulo', 'America/New_York', 'Europe/Madrid', 'UTC',
];
export const COMMON_CURRENCIES = [
    { code: 'COP', label: 'Peso colombiano' },
    { code: 'USD', label: 'Dólar estadounidense' },
    { code: 'EUR', label: 'Euro' },
    { code: 'MXN', label: 'Peso mexicano' },
    { code: 'PEN', label: 'Sol peruano' },
    { code: 'CLP', label: 'Peso chileno' },
];
export const entityDetailsSchema = z.object({
    address: optionalText(250),
    /** Departamento DIVIPOLA (2 dígitos). Con municipio, sale de él. */
    departmentCode: z.string().regex(/^\d{2}$/, 'Departamento no válido').optional().nullable().transform((v) => v || null),
    /** Municipio DIVIPOLA (5 dígitos). */
    municipalityCode: z.string().regex(/^\d{5}$/, 'El código DIVIPOLA del municipio tiene 5 dígitos').optional().nullable().or(z.literal('').transform(() => null)).transform((v) => v || null),
    city: optionalText(120),
    postalCode: optionalText(10),
    phone: optionalText(40),
    email: z.string().trim().toLowerCase().email('Correo no válido').max(320).optional().nullable().or(z.literal('').transform(() => null)).transform((v) => v || null),
    website: z.string().trim().url('Dirección web no válida').max(300).optional().nullable().or(z.literal('').transform(() => null)).transform((v) => v || null),
    timezone: z.string().trim().refine(isTimeZone, 'Zona horaria desconocida').default(DEFAULT_TIMEZONE),
    currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, 'Moneda ISO 4217 de 3 letras').default(DEFAULT_CURRENCY),
    locale: z.string().trim().regex(/^[a-z]{2}(-[A-Z]{2})?$/, 'Idioma como es-CO').default(DEFAULT_LOCALE),
}).superRefine((v, ctx) => {
    if (v.municipalityCode && !municipalityByCode(v.municipalityCode)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['municipalityCode'], message: 'Ese municipio no existe en la DIVIPOLA' });
    }
    if (v.municipalityCode && v.departmentCode && v.municipalityCode.slice(0, 2) !== v.departmentCode) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['municipalityCode'], message: 'El municipio no es de ese departamento' });
    }
}).transform((v) => {
    // Con municipio, el departamento y el nombre de la ciudad salen de la DIVIPOLA.
    const m = v.municipalityCode ? municipalityByCode(v.municipalityCode) : undefined;
    return m ? { ...v, departmentCode: m.departmentCode, city: m.name } : v;
});
export const EMPTY_ENTITY_DETAILS = {
    address: null, departmentCode: null, municipalityCode: null, city: null, postalCode: null, phone: null,
    email: null, website: null, timezone: DEFAULT_TIMEZONE, currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE,
};
export function entityDetailsView(entity, details) {
    // `legalName` de fichas guardadas antes de quitar la razón social no se entrega.
    const { legalName: _legalName, ...d } = { ...EMPTY_ENTITY_DETAILS, ...(details ?? {}) };
    const digits = entity.nit ? nitDigits(entity.nit) : '';
    let dv = null;
    try {
        dv = digits ? nitCheckDigit(digits) : null;
    }
    catch {
        dv = null;
    }
    const dept = d.municipalityCode ? departmentOfMunicipality(d.municipalityCode) : d.departmentCode ? departmentByCode(d.departmentCode) : undefined;
    return {
        ...d,
        departmentCode: dept?.code ?? d.departmentCode,
        name: entity.name,
        legalNameOrName: entity.name,
        nit: digits || null,
        nitDv: dv,
        nitFormatted: digits && dv !== null ? formatNit(digits, dv) : null,
        departmentName: dept?.name ?? null,
    };
}
//# sourceMappingURL=ficha.js.map